/* Consent dialogs for the Suivi dashboard.
 *
 *  - ConsentSheet        — the investigator records what a family member consented to
 *                          OUTSIDE the app (fiche papier SMUR, phone): one
 *                          Oui / Non / Non recueilli choice per option, then how and
 *                          when it was collected.
 *  - AbsenceConfirmModal — « Aucun(e) {{role}} dans ce dossier »: the dossier-level
 *                          declaration that lifts the wait on that role's consent.
 *
 * Both are centered dialogs (ModalKit card, `.resonance-modal`) — a form with pill
 * groups, a date and a note has no business in a bottom sheet, and ModalCard is never
 * used inside one. Vocabulary: « Non recueilli » is a FACT (nothing was collected);
 * « À recueillir » is the call to action on tags — never « non renseigné ».
 *
 * The sheet sends only the options whose choice CHANGED. Re-saving an untouched
 * « Accordé · appli » must never rewrite it as a paper consent dated today; and
 * « Non recueilli » on a previously recorded option clears it — the pill says exactly
 * what will be stored.
 */
import { ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IonModal } from "@ionic/react";
import dayjs from "dayjs";
import {
  ModalCard,
  ModalTitle,
  ModalDescription,
  ModalActions,
  ModalPrimaryButton,
  ModalQuietButton,
  ModalError,
  ModalFields,
} from "../../components/atoms/ModalKit";
import { TextField } from "../../components/atoms/LoginTextField";
import {
  SuiviConsentsBody,
  SuiviConsentStatus,
  SuiviMemberDetail,
} from "../../services/suivi.service";
import { Choice, Choices, FieldLabel, WarnText } from "./ExitModals";

type StaffConsentSource = SuiviConsentsBody["source"];

// Order = how a consent usually reaches the team: the SMUR fiche first, then the phone.
const SOURCES: { id: StaffConsentSource; key: string }[] = [
  { id: "PAPER", key: "suivi_consent_source_paper_choice" },
  { id: "PHONE", key: "suivi_consent_source_phone_choice" },
  { id: "OTHER", key: "suivi_consent_source_other_choice" },
];

const STATUS_CHOICES: { value: SuiviConsentStatus; key: string }[] = [
  { value: "GRANTED", key: "suivi_consent_yes" },
  { value: "REFUSED", key: "suivi_consent_no" },
  { value: null, key: "suivi_consent_unknown" },
];

const today = (): string => dayjs().format("YYYY-MM-DD");

interface ConsentSheetProps {
  isOpen: boolean;
  /** The LIVE member (freshest detail) — its `consents` seed the pills. */
  member: SuiviMemberDetail | null;
  memberName: string;
  saving: boolean;
  error: string | null;
  onDismiss: () => void;
  onConfirm: (body: SuiviConsentsBody) => void;
}

export const ConsentSheet = ({
  isOpen,
  member,
  memberName,
  saving,
  error,
  onDismiss,
  onConfirm,
}: ConsentSheetProps): ReactElement => {
  const { t } = useTranslation();
  const consents = useMemo(() => member?.consents ?? [], [member]);

  const [statuses, setStatuses] = useState<Record<number, SuiviConsentStatus>>({});
  const [source, setSource] = useState<StaffConsentSource>("PAPER");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");

  // Reset on open: the dialog instance is reused across members, and the pills must
  // start from what is recorded for THIS member today. Keyed on the member, not on
  // the consents array: a refetch while the sheet is open must not wipe half-made
  // choices.
  useEffect(() => {
    if (!isOpen) return;
    const seeded: Record<number, SuiviConsentStatus> = {};
    (member?.consents ?? []).forEach((consent) => {
      seeded[consent.consent_option_id] = consent.status;
    });
    setStatuses(seeded);
    setSource("PAPER");
    setDate(today());
    setNote("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, member?.user_id]);

  const statusOf = (optionId: number): SuiviConsentStatus => statuses[optionId] ?? null;
  const changed = consents.filter(
    (consent) => statusOf(consent.consent_option_id) !== consent.status
  );
  const anyRefusal = consents.some(
    (consent) => statusOf(consent.consent_option_id) === "REFUSED"
  );
  // « Parent non vu » reads as selected while every option is « Non recueilli ».
  const noneCollected =
    consents.length > 0 && consents.every((consent) => statusOf(consent.consent_option_id) === null);

  const markNoneCollected = (): void => {
    const cleared: Record<number, SuiviConsentStatus> = {};
    consents.forEach((consent) => {
      cleared[consent.consent_option_id] = null;
    });
    setStatuses(cleared);
  };

  return (
    <IonModal isOpen={isOpen} className="resonance-modal" backdropDismiss onWillDismiss={onDismiss}>
      <ModalCard>
        <ModalTitle>{t("suivi_consent_sheet_title")}</ModalTitle>
        <ModalDescription>{t("suivi_consent_sheet_text", { name: memberName })}</ModalDescription>

        {/* Shortcut for the common case: the parent could not be reached at all. */}
        <Choices>
          <Choice
            type="button"
            $selected={noneCollected}
            aria-pressed={noneCollected}
            onClick={markNoneCollected}
          >
            {t("suivi_consent_not_seen")}
          </Choice>
        </Choices>

        {consents.map((consent) => (
          <div key={consent.consent_option_id}>
            <FieldLabel>{consent.label}</FieldLabel>
            <Choices>
              {STATUS_CHOICES.map((choice) => {
                const selected = statusOf(consent.consent_option_id) === choice.value;
                return (
                  <Choice
                    key={choice.key}
                    type="button"
                    $selected={selected}
                    aria-pressed={selected}
                    onClick={() =>
                      setStatuses((current) => ({
                        ...current,
                        [consent.consent_option_id]: choice.value,
                      }))
                    }
                  >
                    {t(choice.key)}
                  </Choice>
                );
              })}
            </Choices>
          </div>
        ))}

        <FieldLabel>{t("suivi_consent_source_label")}</FieldLabel>
        <Choices>
          {SOURCES.map((candidate) => (
            <Choice
              key={candidate.id}
              type="button"
              $selected={source === candidate.id}
              aria-pressed={source === candidate.id}
              onClick={() => setSource(candidate.id)}
            >
              {t(candidate.key)}
            </Choice>
          ))}
        </Choices>

        <ModalFields>
          {/* Defaults to today — the fiche is usually keyed in the day it comes back. */}
          <TextField
            type="date"
            onChange={(text) => setDate(text)}
            value={date}
            placeholder={t("suivi_consent_date_label")}
            label={t("suivi_consent_date_label")}
          />
          <TextField
            type="text"
            onChange={(text) => setNote(text)}
            value={note}
            placeholder={t("suivi_consent_note_label")}
            label={t("suivi_consent_note_label")}
          />
        </ModalFields>

        {/* A refusal blocks the gated questionnaires but does NOT exit the dossier —
            that stays the investigator's separate decision (opposition shortcut). */}
        {anyRefusal && <WarnText>{t("suivi_consent_refusal_warning")}</WarnText>}

        {error && <ModalError>{error}</ModalError>}

        <ModalActions>
          <ModalQuietButton onClick={onDismiss}>{t("common_cancel")}</ModalQuietButton>
          <ModalPrimaryButton
            disabled={saving || changed.length === 0}
            onClick={() =>
              onConfirm({
                source,
                consented_at: date ? date : null,
                note: note.trim() ? note.trim() : null,
                grants: changed.map((consent) => ({
                  consent_option_id: consent.consent_option_id,
                  status: statusOf(consent.consent_option_id),
                })),
              })
            }
          >
            {t("suivi_consent_confirm")}
          </ModalPrimaryButton>
        </ModalActions>
      </ModalCard>
    </IonModal>
  );
};

interface AbsenceConfirmModalProps {
  isOpen: boolean;
  roleLabel: string;
  saving: boolean;
  error: string | null;
  onDismiss: () => void;
  onConfirm: () => void;
}

/** Confirm before declaring a role absent — the declaration changes what the dossier
 *  waits on, so it is never a one-tap pill (remove-member confirm precedent). */
export const AbsenceConfirmModal = ({
  isOpen,
  roleLabel,
  saving,
  error,
  onDismiss,
  onConfirm,
}: AbsenceConfirmModalProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <IonModal isOpen={isOpen} className="resonance-modal" backdropDismiss onWillDismiss={onDismiss}>
      <ModalCard>
        <ModalTitle>{t("suivi_absence_declare", { role: roleLabel })}</ModalTitle>
        <ModalDescription>{t("suivi_absence_declare_text", { role: roleLabel })}</ModalDescription>

        {error && <ModalError>{error}</ModalError>}

        <ModalActions>
          <ModalQuietButton onClick={onDismiss}>{t("common_cancel")}</ModalQuietButton>
          <ModalPrimaryButton disabled={saving} onClick={onConfirm}>
            {t("suivi_absence_declare_confirm")}
          </ModalPrimaryButton>
        </ModalActions>
      </ModalCard>
    </IonModal>
  );
};
